import { TransferCandidate } from '../../../budget/project-import/types'
import { IWizardProject } from '../../types/wizardData'
import { formatCurrency } from '../../../budget/utils/currencyFormat'

/**
 * Truncates project name to max 20 characters
 * @param name - Name to truncate
 * @returns Truncated name
 * @example
 * truncateProjectName('Very Long Project Name Here') // 'Very Long Project N'
 */
export const truncateProjectName = (name: string): string => {
  return name.slice(0, 20)
}

/**
 * Finds budget by ID
 * @param budgetId - Budget ID to find
 * @param candidates - List of candidates
 * @returns Found budget
 * @throws Error if not found
 * @example
 * const budget = findBudgetById(42, candidates)
 */
export const findBudgetById = (
  budgetId: number,
  candidates: TransferCandidate[]
): TransferCandidate => {
  return candidates.find(b => b.id === budgetId)!
}

/**
 * Maps budget to wizard data
 * @param budget - Transfer candidate
 * @returns Wizard data fields
 * @example
 * const wizardData = mapBudgetToWizardData(budget)
 */
export const mapBudgetToWizardData = (
  budget: TransferCandidate
): Partial<IWizardProject> => {
  return {
    name: truncateProjectName(budget.name),
    customer: budget.client_name,
    start_date: budget.start_date,
    end_date: budget.end_date,
    budgetImportSource: {
      budgetId: budget.id,
      budgetName: budget.name,
      clientName: budget.client_name,
      totalBudget: budget.total_budget
    }
  }
}

/**
 * Formats budget for dropdown display
 * @param budget - Transfer candidate
 * @returns Formatted string
 * @example
 * const label = formatBudgetOption(budget)
 * // "ACME-2024 - Acme Corp (€1.000.000,00)" (with EUR locale)
 */
export const formatBudgetOption = (budget: TransferCandidate): string => {
  return `${budget.name} - ${budget.client_name} (${formatCurrency(budget.total_budget)})`
}

/**
 * Extracts budget position count
 * @param budget - Transfer candidate
 * @returns Number of positions
 * @example
 * const count = getBudgetPositionCount(budget) // 15
 */
export const getBudgetPositionCount = (budget: TransferCandidate): number => {
  return budget.budget_positions.length
}

/**
 * Extracts external position count
 * @param budget - Transfer candidate
 * @returns Number of external positions
 */
export const getExternalPositionCount = (budget: TransferCandidate): number => {
  return budget.external_positions.length
}

/**
 * Extracts phase count
 * @param budget - Transfer candidate
 * @returns Number of phases
 */
export const getPhaseCount = (budget: TransferCandidate): number => {
  return budget.phases.length
}
